import React from "react";
import {
  Box,
  Chip,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Row } from "../types";
import { getBorderStyle, getStylesForMenuItem, MenuProps } from "../styles";
import { Country } from "../../../features/countries/types";
import { getAvailableCountries } from "../utils/getAvailableCountries";
import { Region } from "../../../features/regions/types";

interface MultipleSelectProps {
  rows: Row[];
  row: Row;
  field: keyof Row;
  handleChange?: (event: SelectChangeEvent<string[]>) => void;
  countriesAll: Country[];
  regionsData: Region[];
  canEdit?: boolean;
}

const getFormattedText = <T extends Region | Country>(
  value: string,
  data: T[],
  keyName: keyof T
): string => {
  const foundObject = data.find((item) => item.id === value);
  if (foundObject) {
    return foundObject[keyName] as string;
  }
  return "";
};

const MultipleSelect: React.FC<MultipleSelectProps> = ({
  rows,
  row,
  field,
  handleChange,
  countriesAll,
  canEdit,
  regionsData,
}) => {
  const availableCountries = getAvailableCountries(
    rows,
    row,
    countriesAll,
    regionsData
  );

  const fittedRow = rows.find((r) => r.id === row.id);

  const selectedRegionsOptions =
    field === "region_id" && ([fittedRow?.[field]] as unknown as string[]);

  const selectedCountriesOptions =
    field === "countries" &&
    (fittedRow?.[field].filter((option) =>
      availableCountries.includes(option)
    ) as unknown as string[]);

  const selectedOptions =
    selectedRegionsOptions || selectedCountriesOptions || [];

  return (
    <Select
      key={field}
      multiple={field === "countries"}
      value={selectedOptions}
      onChange={handleChange}
      disabled={
        !canEdit || (field === "countries" && !availableCountries.length)
      }
      fullWidth={true}
      input={
        <OutlinedInput
          id={`select${field === "countries" ? "-multiple" : ""}-${field}`}
          label={field}
        />
      }
      renderValue={(selected) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selected.map((value) => {
            const formattedOptionForRegion = getFormattedText(
              value,
              regionsData,
              "name"
            );
            const formattedOptionForCountry = getFormattedText(
              value,
              countriesAll,
              "country_name"
            );
            return (
              <Chip
                key={`${value}-${field}`}
                label={
                  (field === "region_id"
                    ? formattedOptionForRegion
                    : formattedOptionForCountry) || value
                }
                style={getBorderStyle(
                  field === "region_id"
                    ? formattedOptionForRegion
                    : formattedOptionForCountry
                )}
              />
            );
          })}
        </Box>
      )}
      MenuProps={MenuProps}
    >
      {field === "region_id"
        ? regionsData.map(({ id }) => (
            <MenuItem
              key={id}
              value={id}
              style={getStylesForMenuItem(id, selectedOptions)}
            >
              {getFormattedText(id, regionsData, "name")}
            </MenuItem>
          ))
        : availableCountries.map((option) => (
            <MenuItem
              key={option}
              value={option}
              style={getStylesForMenuItem(option, selectedOptions)}
            >
              {getFormattedText(option, countriesAll, "country_name")}
            </MenuItem>
          ))}
    </Select>
  );
};
export default MultipleSelect;
